import { Badge } from '@/components/common/badge'
import { Note } from '@/components/common/note'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent } from '@/components/common/tabs'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { fromUnixTimestamp } from '@vaultsfyi/common/utils'
import { formatDate, formatPreciseTokenValue } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import type { useForm } from 'react-hook-form'
import { type Address, formatUnits } from 'viem'
import { z } from 'zod'
import { TxSteps } from '../sidePanel/txSteps'

const claimDataSchema = z
  .object({
    pendingWithdrawals: z
      .array(
        z.object({
          assetAmount: z.string(),
          claimTimestamp: z.string(),
        })
      )
      .optional(),
  })
  .optional()

interface Props {
  account: Address | undefined
  stepData: StepData
  onSubmit: () => void
  isSubmitLoading: boolean
  form: ReturnType<typeof useForm>
  vault: Vault
  transactionsContext: TransactionsContext
  currentStep: number
}

export function UpshiftWithdrawalTab({
  account,
  stepData,
  onSubmit: _onSubmit,
  isSubmitLoading,
  form,
  vault,
  transactionsContext,
  currentStep,
}: Props) {
  const claimData = useMemo(() => {
    return claimDataSchema.parse(transactionsContext?.vaultSpecificData as Record<string, any>)
  }, [transactionsContext])

  const pendingWithdrawals = claimData?.pendingWithdrawals
  const claimableAmount = useMemo(() => {
    return pendingWithdrawals?.reduce((acc, claim) => {
      if (fromUnixTimestamp(Number(claim.claimTimestamp)) > Date.now()) return acc
      return acc + BigInt(claim.assetAmount)
    }, 0n)
  }, [pendingWithdrawals])

  const onSubmit = () => {
    form.setValue('amount', formatUnits(BigInt(claimableAmount ?? '0'), vault.asset.decimals), {
      shouldValidate: true,
    })
    _onSubmit()
  }

  const isAnyClaimable = pendingWithdrawals?.some(
    (claim) => fromUnixTimestamp(Number(claim.claimTimestamp)) < Date.now()
  )

  return (
    <form onSubmit={onSubmit}>
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        {pendingWithdrawals ? (
          <>
            <Table>
              <TableHeader>
                <TableRow variant="header">
                  <TableHead className="first:!pl-2">Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="!last:!pr-2">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingWithdrawals.map((claim, index) => {
                  const isClaimable = fromUnixTimestamp(Number(claim.claimTimestamp)) < Date.now()

                  return (
                    <TableRow key={claim.claimTimestamp + index}>
                      <TableCell className="first:!pl-2">
                        {formatPreciseTokenValue({ value: claim.assetAmount, decimals: vault.asset.decimals })}{' '}
                        {vault.asset.symbol}
                      </TableCell>
                      <TableCell>
                        {formatDate({
                          timestamp: fromUnixTimestamp(Number(claim.claimTimestamp)),
                          showYear: true,
                          showTime: true,
                        })}
                      </TableCell>
                      <TableCell className="last:!pr-2">
                        <Badge color={isClaimable ? 'green' : 'orange'}>{isClaimable ? 'Ready' : 'Pending'}</Badge>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </>
        ) : (
          <p className="text-xs">No pending withdrawals</p>
        )}
        <TxSteps
          account={account}
          stepData={stepData}
          activeStep={currentStep}
          disabled={!isAnyClaimable}
          loading={isSubmitLoading}
          label="Claim withdrawal"
          network={vault.network.name}
        />

        {pendingWithdrawals && (
          <Note>
            <p>
              Withdrawals are being processed. Funds will be received automatically or can be claimed manually by the
              processing date.
            </p>
          </Note>
        )}
      </TabsContent>
    </form>
  )
}
